// =======================================================
// File: src/components/Timers/TimerTray.jsx
// Drop-in
// Upgrades:
// - Glassy tray (light/dark friendly), a11y, guards
// - Shows HH:MM:SS live (requestAnimationFrame loop)
// - Keep API as-is: uses useVoiceTimer().active + stopTimer()
// =======================================================
import React, { useEffect, useRef, useState } from "react";
import { useVoiceTimer } from "../../contexts/VoiceTimerContext";

function labelStep(k) {
  switch (k) {
    case "throw":
      return "Throwing";
    case "trim":
      return "Trimming";
    case "attach_handle":
      return "Attach Handle";
    case "glaze":
      return "Glazing";
    default:
      return k || "Working";
  }
}
function fmtHMS(ms) {
  const sec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const two = (n) => String(n).padStart(2, "0");
  return `${two(h)}:${two(m)}:${two(s)}`;
}

export default function TimerTray() {
  const { active, stopTimer } = useVoiceTimer();
  const [now, setNow] = useState(Date.now());
  const rafRef = useRef(0);

  useEffect(() => {
    if (!active) return;
    const tick = () => {
      setNow(Date.now());
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active]);

  if (!active) return null;

  const started = Number(active.startedAt) || Date.now();
  const elapsedMs = Math.max(0, now - started);
  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");

  const frameStyle = isDark
    ? {
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.18)",
      }
    : {
        background: "rgba(255,255,255,0.85)",
        border: "1px solid rgba(0,0,0,0.08)",
      };

  return (
    <div
      className="fixed bottom-4 right-4 z-50 rounded-2xl px-4 py-3 shadow-lg"
      style={{
        ...frameStyle,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      role="status"
      aria-live="polite"
    >
      <div className="text-sm font-semibold">
        {labelStep(active.step)}{" "}
        {active.batchId != null ? (
          <span className="opacity-80">— Batch {String(active.batchId)}</span>
        ) : null}
      </div>
      <div className="text-xs opacity-80">{fmtHMS(elapsedMs)}</div>
      <div className="mt-2 flex gap-2 justify-end">
        <button
          className="px-3 py-1 rounded-xl border hover:bg-white/10"
          onClick={stopTimer}
          title="Stop this timer"
        >
          Stop
        </button>
      </div>
    </div>
  );
}
